// Pure progress math for the running module: distance built toward the 10K, a
// time-toward-goal bar (Kenny = mile time, others = projected 10K finish), and
// whether a given plan slot has been completed this week. No DB, no DOM.
import { TENK_MILES } from './run-plan.js';

const round1 = (n) => Math.round(n * 10) / 10;
const paceSec = (mmss) => { const [m, s] = String(mmss).split(':').map(Number); return (m || 0) * 60 + (s || 0); };

export function distanceBuilt(runs, targetMi = 6.2) {
  // Endurance only — a 1-mile speed test shouldn't advance the distance bar.
  const longest = (runs || [])
    .filter((r) => r.type !== 'timetrial')
    .reduce((m, r) => Math.max(m, Number(r.actual_distance_mi) || 0), 0);
  return { mi: round1(longest), target: targetMi, pct: Math.min(100, Math.round((longest / targetMi) * 100)) };
}

// Kenny: mile time -> goal from a 650s (10:50) anchor. Others: projected 10K finish -> goal.
export function timeProgress(user, runs = [], goals = []) {
  if (user === 'kenny') {
    const mile = goals.find((g) => g.kind === 'mile_time');
    const cur = Number(mile?.current || 650), goal = Number(mile?.target || 450), anchor = 650;
    const pct = Math.max(0, Math.min(100, Math.round(((anchor - cur) / (anchor - goal)) * 100)));
    return { kind: 'mile', curSec: cur, goalSec: goal, pct, has: runs.some((r) => r.type === 'timetrial') };
  }
  const race = goals.find((g) => g.kind === 'race_10k');
  const goalMin = Number(race?.target || 75);
  const paced = (runs || []).filter((r) => r.avg_pace && (r.type === 'easy' || r.type === 'long')).slice(0, 3);
  if (!paced.length) return { kind: 'finish', has: false, goalMin, projMin: null, pct: 0 };
  const avgPace = paced.reduce((s, r) => s + paceSec(r.avg_pace), 0) / paced.length;
  const projMin = round1((avgPace * TENK_MILES) / 60);
  const anchorMin = goalMin * 1.35; // rough starting point so the bar has room to grow
  const pct = Math.max(0, Math.min(100, Math.round(((anchorMin - projMin) / (anchorMin - goalMin)) * 100)));
  return { kind: 'finish', has: true, goalMin, projMin, pct };
}

// A plan slot is done if a run this week is tagged to it, or (fallback) an untagged
// non-HIIT run lands on that slot's weekday within the week.
export function planSlotDone(runs, { weekStartISO, weekEndISO, weekday, slot }) {
  return (runs || []).some((r) => {
    if (!r.date || r.date < weekStartISO || r.date > weekEndISO) return false;
    if (r.planned?.slot) return r.planned.slot === slot;
    const [y, m, d] = r.date.split('-').map(Number);
    const wd = (() => { const g = new Date(y, m - 1, d).getDay(); return g === 0 ? 7 : g; })();
    return wd === weekday && r.type !== 'hiit';
  });
}

// Minutes of cardio logged in one week, and how much of it was running. The book's
// dose is 1-3 hours a week with running no more than half (p.333-336).
export function weeklyCardio(runs, { weekStartISO, weekEndISO }) {
  let total = 0; let running = 0;
  for (const r of runs || []) {
    if (!r.date || r.date < weekStartISO || r.date > weekEndISO) continue;
    const min = Number(r.actual_minutes) || 0;
    total += min;
    if (r.type !== 'lowimpact' && r.type !== 'hiit') running += min;
  }
  return { total: Math.round(total), running: Math.round(running), runningPct: total ? Math.round((running / total) * 100) : 0 };
}
